import {mapObject} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import ChildImportPlanSection from 'config-migration/import/import-plan-sections/ChildImportPlanSection';
import {LogLevel} from 'config-migration/migration-log/LogLevel';
import {MigrationError} from 'config-migration/MigrationConstants';
import {PrimaryObjectPlan} from 'config-migration/types/MigrationObjects';
import {PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT, PrimaryObjectType} from 'config-migration/types/ObjectType';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import uniqueId from 'util/uniqueId';

import {ObjectAction, ObjectActionType} from '../../types/objectActions';
import {
  ImportPlanSectionRowWrapper,
  ObjectDisplayNameColumn,
} from '../import-plan-sections/ImportPlanSectionRow';
import {getActionDescription, getActionOption, getOptionValue} from '../ObjectActionDropdown';

export interface TemplatePlanRowProps {
  plan: PrimaryObjectPlan;
  errors: Array<MigrationError>;
  onUpdatePlan: (plan: PrimaryObjectPlan) => void;
  templateCollectionPlan: PrimaryObjectPlan;
}

export default function TemplatePlanRow(props: TemplatePlanRowProps): JSX.Element {
  const {plan, errors} = props;
  const hasErrors = errors.filter(({level}) => level === LogLevel.ERROR).length > 0;
  const hasWarnings = errors.filter(({level}) => level === LogLevel.WARNING).length > 0;
  const hasChildren = Boolean(plan.childMigrationObjectsByType.size);
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(true);
  const icon = iconForObjectType(props.plan.objectType);
  const label = labelForObjectType(props.plan.objectType);
  return (
    <>
      <ImportPlanSectionRowWrapper
        plan={plan}
        hasErrors={hasErrors}
        hasWarnings={hasWarnings}
        key={plan.objectId}
        nestedDepth={0}
      >
        <ObjectDisplayNameColumn
          hasErrors={hasErrors}
          hasWarnings={hasWarnings}
          rowIcon={icon}
          subLabel={label}
          plan={plan}
          errors={errors}
          setIsCollapsed={setIsCollapsed}
          isCollapsed={isCollapsed}
          hasChildren={hasChildren}
          highImpactChangeCount={0}
        />
        <TemplateActionDropdownColumn
          plan={plan}
          onUpdatePlan={props.onUpdatePlan}
          templateCollectionPlan={props.templateCollectionPlan}
        />
      </ImportPlanSectionRowWrapper>
      {!isCollapsed &&
        hasChildren &&
        Array.from(plan.childMigrationObjectsByType.keys()).map((childType) => (
          <ChildImportPlanSection
            plan={props.plan}
            childType={childType}
            onUpdatePlan={props.onUpdatePlan}
            nestedDepth={1}
            highImpactChangeMap={new Map()}
            key={childType}
          />
        ))}
    </>
  );
}

function iconForObjectType(objectType: PrimaryObjectType): BenchlingIconName | undefined {
  switch (objectType) {
    case PrimaryObjectType.Template:
      return ModelTypeConstants.TEMPLATE;
    case PrimaryObjectType.Subtemplate:
      return ModelTypeConstants.SUBTEMPLATE;
    case PrimaryObjectType.Procedure:
      return ModelTypeConstants.PROCEDURE;
    default:
      return undefined;
  }
}

function labelForObjectType(objectType: PrimaryObjectType): string {
  switch (objectType) {
    case PrimaryObjectType.Template:
      return PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[PrimaryObjectType.Template];
    case PrimaryObjectType.Subtemplate:
      return PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[PrimaryObjectType.Subtemplate];
    case PrimaryObjectType.Procedure:
      // TODO(BNCH-109180): This should be more specific and depend on the procedure sub type.
      return PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[PrimaryObjectType.Procedure];
    default:
      return '';
  }
}

function TemplateActionDropdownColumn(props: {
  plan: PrimaryObjectPlan;
  onUpdatePlan: (plan: PrimaryObjectPlan) => void;
  templateCollectionPlan: PrimaryObjectPlan;
}): JSX.Element {
  const onUpdateAction = (selectedAction: ObjectAction): void => {
    props.onUpdatePlan({...props.plan, selectedAction});
  };
  return (
    <div className="simpleDivTable-cell">
      <SelectDropdown
        className="u-mini-spacing-right u-flex-grow"
        disabled={props.templateCollectionPlan.selectedAction.type === ObjectActionType.IGNORE}
        menu={
          <Menu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            value={getOptionValue(props.plan.selectedAction)}
            scrollable={true}
          >
            <Section>
              {getActionOption({type: ObjectActionType.IGNORE}, onUpdateAction)}
              {!props.plan.hasProvenanceUriMatch &&
                getActionOption({type: ObjectActionType.CREATE}, onUpdateAction)}
            </Section>
            {props.plan.initialAction.type === ObjectActionType.UPDATE && props.plan.hasProvenanceUriMatch
              ? getTemplateUpdateSectionForCollection(
                  props.plan.initialAction.target.parentIdentifier!.displayName,
                  [props.plan.initialAction],
                  onUpdateAction
                )
              : getTemplateUpdateOptions(props.plan, onUpdateAction, props.templateCollectionPlan)}
          </Menu>
        }
        displayName={getActionDescription(props.plan.selectedAction)}
      />
    </div>
  );
}

function getTemplateUpdateSectionForCollection(
  collectionName: string,
  actions: Array<ObjectAction>,
  onSelectAction: (selectedAction: ObjectAction) => void
): JSX.Element {
  return (
    <Section key={uniqueId('template-update-')}>
      <SectionHeader>
        <BenchlingIcon icon={ModelTypeConstants.TEMPLATE_COLLECTION} />
        <span className="u-tiny-spacing-left">{collectionName}</span>
      </SectionHeader>
      {actions.map((action) => getActionOption(action, onSelectAction))}
    </Section>
  );
}

function getTemplateUpdateOptions(
  plan: PrimaryObjectPlan,
  onUpdatePlanAction: (selectedAction: ObjectAction) => void,
  templateCollectionPlan: PrimaryObjectPlan
): Array<JSX.Element> {
  // Group the actions by collection, and figure out the name of each collection.
  const actionsByCollectionId = _.groupBy(
    plan.additionalActions,
    (action) => action.target.parentIdentifier!.apiIdentifier
  );
  const collectionApiIdentifierToName = mapObject(
    actionsByCollectionId,
    (plans) => plans[0].target.parentIdentifier!.displayName
  );

  // Figure out if the given template is being placed in an existing collection that is being updated.
  const updatingTemplateCollectionId =
    templateCollectionPlan.selectedAction.type === ObjectActionType.UPDATE &&
    templateCollectionPlan.selectedAction.target.apiIdentifier;

  // Create menu sections for each collection, sorted by collection name and excluding the collection being
  // updated, which we'll bubble up to the top.
  const nameSortedCollectionIds = _.sortBy(
    Object.keys(actionsByCollectionId).filter(
      (collectionId) => !updatingTemplateCollectionId || collectionId !== updatingTemplateCollectionId
    ),
    (collectionId) => collectionApiIdentifierToName[collectionId].toLowerCase()
  );

  const nameSortedCollectionSections = nameSortedCollectionIds.map((collectionId) =>
    getTemplateUpdateSectionForCollection(
      collectionApiIdentifierToName[collectionId],
      actionsByCollectionId[collectionId],
      onUpdatePlanAction
    )
  );

  if (updatingTemplateCollectionId && actionsByCollectionId[updatingTemplateCollectionId]) {
    return [
      getTemplateUpdateSectionForCollection(
        collectionApiIdentifierToName[updatingTemplateCollectionId],
        actionsByCollectionId[updatingTemplateCollectionId],
        onUpdatePlanAction
      ),
      ...nameSortedCollectionSections,
    ];
  }
  return nameSortedCollectionSections;
}
